/**
 * Abstraction de transport.
 *
 * Le moteur de transfert ne connaît que cette interface — jamais `RTCDataChannel`,
 * jamais un WebSocket. Conséquence directe : brancher le relais de secours (Phase 4)
 * se limite à fournir une seconde implémentation, sans toucher au code d'envoi ou de
 * réception. C'est le point d'extension qui a motivé cette abstraction dès le MVP.
 *
 * ## Le contrôle de flux est le cœur du problème
 *
 * Écrire dans le canal plus vite qu'il ne draine fait grossir le tampon interne jusqu'à
 * saturer la mémoire de l'onglet. C'est la cause première des échecs sur gros fichiers.
 * D'où une interface qui **expose explicitement** la pression : `bufferedAmount` et
 * `waitForDrain()`. Un transport qui cacherait cette information rendrait le débit
 * non régulé par construction.
 */

import { HIGH_WATER, LOW_WATER, type Bytes } from '@attrape/shared';

export type TransportKind = 'datachannel' | 'relay';

export interface Transport {
  readonly kind: TransportKind;
  readonly isOpen: boolean;

  /** Octets en attente d'émission dans le tampon sous-jacent. */
  readonly bufferedAmount: number;

  /** Émet une trame déjà encodée. Peut mettre en tampon. */
  send(frame: Bytes): void;

  /**
   * Attend que le tampon soit redescendu sous le seuil bas.
   * Résolue immédiatement si le tampon est déjà bas.
   */
  waitForDrain(signal?: AbortSignal): Promise<void>;

  /** Enregistre un gestionnaire de réception. Renvoie une fonction de désabonnement. */
  onMessage(handler: (frame: Bytes) => void): () => void;

  /** Enregistre un gestionnaire de fermeture. Renvoie une fonction de désabonnement. */
  onClose(handler: () => void): () => void;

  close(): void;
}

export class DataChannelTransport implements Transport {
  readonly kind = 'datachannel' as const;

  constructor(private readonly channel: RTCDataChannel) {
    // `arraybuffer` donne un `ArrayBuffer` directement exploitable, sans passer par un
    // `Blob` dont la lecture asynchrone ajouterait une copie par trame.
    this.channel.binaryType = 'arraybuffer';
    // Le seuil doit être posé sur le canal : c'est lui qui décide quand émettre
    // l'événement `bufferedamountlow`.
    this.channel.bufferedAmountLowThreshold = LOW_WATER;
  }

  get isOpen(): boolean {
    return this.channel.readyState === 'open';
  }

  get bufferedAmount(): number {
    return this.channel.bufferedAmount;
  }

  /** Le seuil à partir duquel l'émetteur doit marquer une pause. */
  get highWaterMark(): number {
    return HIGH_WATER;
  }

  send(frame: Bytes): void {
    this.channel.send(frame);
  }

  waitForDrain(signal?: AbortSignal): Promise<void> {
    if (this.channel.readyState !== 'open') {
      return Promise.reject(new Error('Canal fermé'));
    }
    if (this.channel.bufferedAmount <= LOW_WATER) {
      return Promise.resolve();
    }

    return new Promise<void>((resolve, reject) => {
      const channel = this.channel;

      function cleanup(): void {
        channel.removeEventListener('bufferedamountlow', onLow);
        channel.removeEventListener('close', onClose);
        signal?.removeEventListener('abort', onAbort);
      }

      function onLow(): void {
        cleanup();
        resolve();
      }

      function onClose(): void {
        cleanup();
        reject(new Error('Canal fermé pendant l’attente de drainage'));
      }

      function onAbort(): void {
        cleanup();
        reject(new DOMException('Transfert annulé', 'AbortError'));
      }

      channel.addEventListener('bufferedamountlow', onLow);
      channel.addEventListener('close', onClose);
      signal?.addEventListener('abort', onAbort, { once: true });

      // Cas limite : le tampon a pu se vider entre le test initial et l'abonnement.
      if (channel.bufferedAmount <= LOW_WATER) onLow();
    });
  }

  onMessage(handler: (frame: Bytes) => void): () => void {
    const listener = (event: MessageEvent): void => {
      const data: unknown = event.data;
      if (data instanceof ArrayBuffer) {
        handler(new Uint8Array(data));
        return;
      }
      // Un pair qui enverrait du texte sur un canal censé être binaire : on ignore
      // plutôt que de lever, pour ne pas transformer un message parasite en panne.
    };

    this.channel.addEventListener('message', listener);
    return () => this.channel.removeEventListener('message', listener);
  }

  onClose(handler: () => void): () => void {
    const listener = (): void => handler();
    this.channel.addEventListener('close', listener);
    return () => this.channel.removeEventListener('close', listener);
  }

  close(): void {
    this.channel.close();
  }
}
